import {
	CalendarAppointmentsLoadStatus,
	CalendarAppointmentsResult,
	CalendarColor,
	getAppointments
} from "client/Api/Calendar/calendar";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { parseDtoDate, formatForDto } from "client/DateAndTime/dateTimeUtils";
import {
	addDays,
	convertToDateTime,
	parseDtoDateOnlyIgnoringOffset
} from "client/DateAndTime/dateUtils";
import { TimeZone } from "client/DateAndTime/timezones";
import { AlertManager, useAlerts } from "client/Hooks";
import { ensureNotNil, isNotNil } from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	Dispatch,
	RefObject,
	SetStateAction,
	useCallback,
	useMemo,
	useRef,
	useState
} from "react";
import { CalendarAppointment } from "./CalendarPage";
import { setClientId } from "client/Misc/clientId";
import { never } from "client_legacy/ndaw/errors";

export type UseAppointmentLoadingResult = {
	resetAppointmentLoading: (range: readonly DateOnly[], timezone: TimeZone) => void;
	enqueueAppointmentRetrieval: (calendars: readonly CalendarForAppointmentLoading[]) => void;
	cancelAppointmentRetrieval: (calendarId: string) => void;
	loadingStatusMap: ReadonlyMap<string, LoadingStatus>;
};

export function useAppointmentLoading(
	setAppointments: Dispatch<SetStateAction<readonly CalendarAppointment[]>>
): UseAppointmentLoadingResult {
	const [loadingStatusMap, setLoadingStatusMap] = useState<ReadonlyMap<string, LoadingStatus>>(new Map());
	const appointmentLoadingManagerStateRef: RefObject<AppointmentLoadingManagerState | null> = useRef<AppointmentLoadingManagerState>(null);

	const alertManager: AlertManager = useAlerts();

	const setLoadingStatus = useCallback(
		(calendarIds: readonly string[], status: LoadingStatus): void => {
			setLoadingStatusMap(
				(x): ReadonlyMap<string, LoadingStatus> => {
					const result = new Map<string, LoadingStatus>(x);

					for (const calendarId of calendarIds) {
						result.set(calendarId, status);
					}

					return result;
				}
			);
		},
		[]
	);

	const reset = useCallback(
		(range: readonly DateOnly[], timezone: TimeZone): void => {
			if (range.length === 0) {
				throw new Error(`Invalid range '${JSON.stringify(range)}'`);
			}

			const { current: loadingState } = appointmentLoadingManagerStateRef;

			if (loadingState) {
				loadingState.pending = [];
				// We don't abort the "doing"'s, since the assumption is that they are linked to the "global" cancellation
				loadingState.cancellation
					.abort();
			}

			setLoadingStatusMap(new Map<string, LoadingStatus>());
			setAppointments([]);

			const start: Date = convertToDateTime(range[0], timezone);
			const end: Date = convertToDateTime(
				addDays(
					range[range.length - 1],
					1
				),
				timezone
			);

			appointmentLoadingManagerStateRef.current = {
				cancellation: new AbortController(),
				timezone,
				start: formatForDto(start, timezone),
				end: formatForDto(end, timezone),
				doing: new Map<string, AbortController>(),
				pending: []
			};
		},
		[setAppointments]
	);

	const processResult = useCallback(
		(
			result: CalendarAppointmentsResult,
			timeZone: TimeZone,
			color: CalendarColor,
			canEdit: boolean
		): void => {
			const calendarId: string = result.id;

			switch (result.status) {
				case CalendarAppointmentsLoadStatus.NoError:
					const retrievedAppointments: readonly CalendarAppointment[] = result.appointments
						.map(
							(a): CalendarAppointment => {
								const start: Date = parseDate(
									a.start,
									a.isAllDayEvent,
									timeZone
								);

								const end: Date = parseDate(
									a.end,
									a.isAllDayEvent,
									timeZone
								);

								return setClientId({
									...a,
									start,
									end,
									color,
									calendarId,
									canEdit,
									kind: "regular"
								});
							}
						);

					setAppointments(
						(x) => {
							const appointments: readonly CalendarAppointment[] = x.filter(
								a => a.kind === "regular" && a.calendarId !== calendarId
							);

							return [
								...appointments,
								...retrievedAppointments
							];
						}
					);

					setLoadingStatus([calendarId], "success");
					break;
				case CalendarAppointmentsLoadStatus.UnknownError:
				case CalendarAppointmentsLoadStatus.AccessDenied:
					alertManager.showError(
						localize(
							statusToErrorMessageMap[result.status]
						)
					);

					setLoadingStatus([calendarId], statusToLoadingStatusMap[result.status]);
					break;
				default:
					never(result.status);

			}
		},
		[
			alertManager,
			setAppointments,
			setLoadingStatus
		]
	);

	const retrieveAppointments = useCallback(
		async (calendar: CalendarForAppointmentLoading, loadingState: AppointmentLoadingManagerState): Promise<void> => {
			const globalSignal: AbortSignal = loadingState.cancellation
				.signal;

			const { id, color, canEdit } = calendar;

			if (!globalSignal.aborted) {
				try {
					const cancellation = new AbortController();

					globalSignal.addEventListener(
						"abort",
						(): void => cancellation.abort(globalSignal.reason),
						{ once: true, signal: cancellation.signal }
					);

					loadingState.doing
						.set(id, cancellation);

					const loadingResult: CalendarAppointmentsResult = await getAppointments(
						{
							id,
							start: loadingState.start,
							end: loadingState.end
						},
						cancellation.signal
					);

					processResult(
						loadingResult,
						loadingState.timezone,
						color,
						canEdit
					);
				}
				catch (e) {
					if (!isCancelledError(e)) {
						alertManager.showError(
							localize("_ErrorDuringAppointmentRetrieval")
						);

						setLoadingStatus([id], "error");
					}
				}
			}

			loadingState.doing
				.delete(id);

			const pending: CalendarForAppointmentLoading | undefined = loadingState.pending
				.shift();

			if (isNotNil(pending)) {
				retrieveAppointments(pending, loadingState);
			}
		},
		[
			alertManager,
			processResult,
			setLoadingStatus
		]
	);

	const enqueue = useCallback(
		(calendars: readonly CalendarForAppointmentLoading[]): void => {
			const loadingState: AppointmentLoadingManagerState = ensureNotNil(appointmentLoadingManagerStateRef.current);

			setLoadingStatus(
				calendars.map((x) => x.id),
				"loading"
			);

			const MAX_PARALLELISM: number = 2;
			const amountToDirectlyStart: number = Math.max(0, MAX_PARALLELISM - loadingState.doing.size);

			const toStart: readonly CalendarForAppointmentLoading[] = calendars.slice(0, amountToDirectlyStart);
			const toQueue: readonly CalendarForAppointmentLoading[] = calendars.slice(amountToDirectlyStart);

			// When a calendar gets enabled by the user, he/she is probably most interested at that one
			// so we put it to the front of the queue
			loadingState.pending = [...toQueue, ...loadingState.pending];

			for (const calendarAndColor of toStart) {
				retrieveAppointments(calendarAndColor, loadingState);
			}
		},
		[retrieveAppointments, setLoadingStatus]
	);

	const cancel = useCallback(
		(calendarId: string): void => {
			setLoadingStatusMap(
				(x): ReadonlyMap<string, LoadingStatus> => {
					const result = new Map<string, LoadingStatus>(x);
					result.delete(calendarId);
					return result;
				}
			);

			const { current: loadingState } = appointmentLoadingManagerStateRef;
			if (!loadingState) {
				return;
			}

			loadingState.pending = loadingState.pending
				.filter((x) => x.id !== calendarId);

			loadingState.doing
				.get(calendarId)
				?.abort();
		},
		[]
	);

	return useMemo(
		(): UseAppointmentLoadingResult => ({
			resetAppointmentLoading: reset,
			enqueueAppointmentRetrieval: enqueue,
			cancelAppointmentRetrieval: cancel,
			loadingStatusMap
		}),
		[
			cancel,
			loadingStatusMap,
			enqueue,
			reset
		]
	);
}

export type LoadingStatus = "loading" | "error" | "access-denied" | "success";

type AppointmentLoadingManagerState = {
	cancellation: AbortController;
	start: string;
	end: string;
	timezone: TimeZone;
	doing: Map<string, AbortController>;
	pending: CalendarForAppointmentLoading[];
};

function parseDate(
	date: string,
	isAllDayEvent: boolean,
	timezone: TimeZone
): Date {
	// We want to render all day events for the date they were created no matter timezone
	// for example if all day event was created on 2023-08-09 in Amsterdam
	// it should be rendered on this date in any other timezone
	return isAllDayEvent
		? convertToDateTime(
			ensureNotNil(
				parseDtoDateOnlyIgnoringOffset(date)
			),
			timezone
		)
		: parseDtoDate(date);
}

export type CalendarForAppointmentLoading = {
	id: string;
	color: CalendarColor;
	canEdit: boolean;
};

const statusToErrorMessageMap: Record<Exclude<CalendarAppointmentsLoadStatus, CalendarAppointmentsLoadStatus.NoError>, LocalizationKey> = {
	[CalendarAppointmentsLoadStatus.AccessDenied]: "_NoPermissionsToReadCalendar",
	[CalendarAppointmentsLoadStatus.UnknownError]: "_ErrorDuringAppointmentRetrieval"
};

const statusToLoadingStatusMap: Record<Exclude<CalendarAppointmentsLoadStatus, CalendarAppointmentsLoadStatus.NoError>, LoadingStatus> = {
	[CalendarAppointmentsLoadStatus.AccessDenied]: "access-denied",
	[CalendarAppointmentsLoadStatus.UnknownError]: "error"
};
